// @flow

// Libs
import _isEmpty from 'lodash/isEmpty'
import _get from 'lodash/get'
import _find from 'lodash/find'
import { createSelector } from 'reselect'

// DataTable Composer
import composeDataTable from 'fc-component-datatable'

// Hocs
import assetsDataTableHoc from './hocs/AssetsDataTable'

// Selectors
import { areaPerAsset$ } from 'fc-assets'
import seasonSpecificFields$ from 'hfty-assets-dom/src/selectors/seasonSpecificFields'
import { assetsOfCurrentSeason$ } from 'hfty-assets-dom/src/selectors/assets'

// Constants
import {
  PROP_ASSET_ID,
  PROP_LABEL,
  PROP_CATEGORY,
  PROP_SHAPE,
  PROP_PARENT,
  FARM,
} from 'fc-model-assets'
import {
  DATATABLE_NAME,
  ATTR_ASSET_ID,
  ATTR_LABEL,
  ATTR_CATEGORY,
  ATTR_SHAPE,
  ATTR_AREA,
  ATTR_PARENT_ID,
  ATTR_HAS_ACTIVE_MAP_VIEW,
  ATTR_GROUP_BY_BOUNDARY_TYPE,
} from './constants'

// Types
import type { Asset } from 'fc-model-assets/types'
import type { AssetRecord } from '../types'
import type {
  DataTableState,
  DataTableModule,
} from 'fc-component-datatable/types'
type ComposeAssetsDataTableArgs = {
  assets$: (state: Object) => Array<Asset>,
  assetsOfCurrentSeason$: (state: Object) => Array<Asset>,
  tableState$: (state: Object) => DataTableState,
}

// Exports
export default function composeAssetsDataTable ({
  assets$,
  tableState$,
  }: ComposeAssetsDataTableArgs): DataTableModule<Asset, AssetRecord> {
  //
  function makeRecords (records$: (state: Object) => Array<Asset>): (state: Object) => Array<AssetRecord> {
    return createSelector(
      [records$, areaPerAsset$, seasonSpecificFields$, assetsOfCurrentSeason$],
      function (
        assets: Array<Asset>,
        areaPerAsset: { [number]: number },
        seasonSpecificFields: Object,
        assetsOfCurrentSeason$: Array<Asset>,
      ): Array<AssetRecord> {
        console.warn('Recompute Records')
        return assets.map((asset: Asset): AssetRecord => {
          const assetId = asset[PROP_ASSET_ID]
          const parent = _find(assetsOfCurrentSeason$, {[PROP_ASSET_ID]: asset[PROP_PARENT]})
          const isFarm = asset[PROP_CATEGORY] === FARM
          const isFarmHasFields = isFarm && _get(seasonSpecificFields, `[${assetId}].length`, 0) > 0
          const hasShape = !_isEmpty(asset[PROP_SHAPE])
          const isMapActive = isFarmHasFields || hasShape

          return {
            [ATTR_ASSET_ID]: assetId,
            [ATTR_LABEL]: asset[PROP_LABEL],
            [ATTR_CATEGORY]: asset[PROP_CATEGORY],
            [ATTR_SHAPE]: asset[PROP_SHAPE],
            [ATTR_AREA]: areaPerAsset[assetId] ? `${areaPerAsset[assetId]} ac` : 0,
            [ATTR_PARENT_ID]: parent ? parent[PROP_ASSET_ID] : null,
            [ATTR_HAS_ACTIVE_MAP_VIEW]: isMapActive,
            [ATTR_GROUP_BY_BOUNDARY_TYPE]: asset[PROP_SHAPE] ? _get(asset, `[${PROP_SHAPE}].shapeData.type`, '') : '',
          }
        })
      }
    )
  }

  const datatable: DataTableModule<Asset, AssetRecord> = composeDataTable({name: DATATABLE_NAME})({
    data$: assets$,
    tableState$,
    makeRecords,
  })

  return {
    ...datatable,
    DataTable: assetsDataTableHoc(datatable.DataTable),
  }
}
